import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ToastId,
  useErrorToast,
  usePendingToast,
  useShowToast,
} from "adminComponents/utils/toast";
import { useAnalytics } from "lib/contexts/analytics";
import { useHubSpot } from "lib/hooks/useHubSpot";
import { useCopyPracticeSet } from "links/lib/features/practiceSets";
import { ICopyPracticeSetResponse } from "links/lib/features/practiceSets/useCopyPracticeSet";
import { AnalyticsEvent, IPracticeSet } from "links/lib/types";

export interface IResult {
  isCopiedBySetId: { [copiedSetId: string]: boolean };
  isLoading: boolean;
  handleCopySet: (practiceSet: IPracticeSet) => void;
}

export const useCopyToMyLibrary = (): IResult => {
  const [isCopiedBySetId, setIsCopiedBySetId] = useState<{
    [copiedSetId: string]: boolean;
  }>({});
  const { showPendingToast, closePendingToast } = usePendingToast();
  const pendingToastId = useRef<ToastId | undefined>();
  const showToast = useShowToast();
  const { trackEvent } = useAnalytics();
  const { trackHubSpotEvent } = useHubSpot();
  const { t } = useTranslation("admin", {
    useSuspense: false,
    keyPrefix: "publicLibraryContainer.practiceSetDetail.copyToMyLibrary",
  });

  const onSuccess = (data: ICopyPracticeSetResponse) => {
    setIsCopiedBySetId((isCopiedBySetId) => {
      if (data.practice_set?.copied_from_set_id) {
        isCopiedBySetId[data.practice_set?.copied_from_set_id] = true;
      }
      return isCopiedBySetId;
    });

    showToast(t("successToast"));
    trackHubSpotEvent(AnalyticsEvent.HubSpot_CopiedPublicSet);
  };

  const onSettled = () => {
    closePendingToast(pendingToastId.current);
    pendingToastId.current = undefined;
  };

  const copyPracticeSet = useCopyPracticeSet({ onSuccess, onSettled });

  useErrorToast(copyPracticeSet.error);

  const handleCopySet = (practiceSet: IPracticeSet) => {
    copyPracticeSet.mutate({ id: practiceSet.id });

    closePendingToast(pendingToastId.current);
    pendingToastId.current = showPendingToast(t("pendingToast"));

    trackEvent(
      AnalyticsEvent.TeacherDashboard_PublicLibrary_PracticeSetDetail_CopyPracticeSet,
      {
        practiceSetId: practiceSet.id,
        isCertified: practiceSet.is_certified,
        isPremium: practiceSet.is_premium,
      }
    );
  };

  return {
    handleCopySet,
    isCopiedBySetId,
    isLoading: copyPracticeSet.isLoading,
  };
};
